import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, SubmitHandler, FieldValues, Controller } from 'react-hook-form';
import {
  Button,
  FormLabel,
  Grid,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Typography
} from '@material-ui/core';
import { isEmptyObject } from 'jquery';

import { useStyles } from './styles';
import { Modal } from 'components/migrated/modal';
import { assignorFormSchema } from '../../validations';
import { AssignorIndicator, AssignorRegistrationProps } from './types';
import { useSupplierContext } from 'contexts/complementaryRegistration';
import { OperationAssignorInput } from '../../graphql/registerSupplier/types';
import { CNPJTextMaskCustom, CPFTextMaskCustom, DDDTextMaskCustom, PhoneTextMaskCustom } from '../../formCustomMasks';

const assignorIndicators: AssignorIndicator[] = [
  {
    name: 'isSoleSigner',
    label: 'Assina Sozinho?',
    value: 'N'
  },
  {
    name: 'inSignsCessionAgreement',
    label: 'Assina Termo de Cessão?',
    value: 'N'
  },
  {
    name: 'inEndorsementSigner',
    label: 'Assina por Endosso?',
    value: 'N'
  },
  {
    name: 'inGuarantor',
    label: 'Avalista?',
    value: 'N'
  },
  {
    name: 'inIssuesPromissoryNote',
    label: 'Emite Duplicata?',
    value: 'N'
  },
  {
    name: 'inRelatedParty',
    label: 'Parte Relacionada?',
    value: 'N'
  },
  {
    name: 'inRepresentative',
    label: 'Representante?',
    value: 'N'
  }
];

export const AssignorRegistration = ({
  personIdLogged,
  isAssinorRegistrationModalOpen,
  onClose,
  editMode,
  assignorData
}: AssignorRegistrationProps) => {
  const classes = useStyles();
  const { setSupplierData, setHasChanges } = useSupplierContext();

  const {
    control: controlAssignor,
    handleSubmit: handleSubmitAssignor,
    setValue: setValueAssignor,
    reset: resetAssignor,
    formState: formStateAssignor,
    watch: watchAssignor
  } = useForm<FieldValues>({
    mode: 'onBlur',
    resolver: yupResolver(assignorFormSchema)
  });

  const [selectedValue, setSelectedValue] = useState('naturalPerson');

  const { isDirty: isDirtyAssignor, dirtyFields: dirtyFieldsAssignor } = formStateAssignor;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldToClear = event.target.value === 'naturalPerson' ? 'cnpj' : 'cpf';
    const field = event.target.value === 'naturalPerson' ? 'cpf' : 'cnpj';

    if (watchAssignor(fieldToClear)) {
      setValueAssignor(field, '');
    }

    setSelectedValue(event.target.value);
  };

  const handleOnSubmit: SubmitHandler<FieldValues> = async (values) => {
    setSupplierData((prevSupplierData) => {
      const assignorToUpdateIndex = prevSupplierData.assignors.findIndex((assignorItem) => {
        if (values.idFCed) {
          return assignorItem.item.idFCed === values.idFCed;
        } else if (assignorData) {
          return (
            (assignorItem.item.cpf === assignorData.cpf || assignorItem.item.cnpj === assignorData.cnpj) &&
            assignorItem.item.fullName === assignorData.fullName
          );
        } else {
          return false;
        }
      });

      const updatedAssignorArray = [...(prevSupplierData.assignors as OperationAssignorInput)];
      const objectHasChanges = !isEmptyObject(dirtyFieldsAssignor);

      if (assignorToUpdateIndex !== -1) {
        updatedAssignorArray[assignorToUpdateIndex] = {
          operation: objectHasChanges ? (values.idFCed ? 'A' : 'I') : '',
          item: {
            ...updatedAssignorArray[assignorToUpdateIndex].item,
            cnpj: values.cnpj ? values.cnpj.replace(/\D/g, '') : null,
            cpf: values.cpf ? values.cpf.replace(/\D/g, '') : null,
            fullName: values.fullName,
            personalEmail: values.personalEmail,
            areaCode: values.areaCode,
            phone: values.phone.replace(/[-\s]/g, ''),
            isSoleSigner: values.isSoleSigner,
            inSignsCessionAgreement: values.inSignsCessionAgreement,
            inEndorsementSigner: values.inEndorsementSigner,
            inGuarantor: values.inGuarantor,
            inIssuesPromissoryNote: values.inIssuesPromissoryNote,
            inRelatedParty: values.inRelatedParty,
            inRepresentative: values.inRepresentative
          }
        };
      } else {
        updatedAssignorArray.push({
          operation: 'I',
          item: {
            cnpj: values.cnpj ? values.cnpj.replace(/\D/g, '') : null,
            cpf: values.cpf ? values.cpf.replace(/\D/g, '') : null,
            fullName: values.fullName,
            personalEmail: values.personalEmail,
            areaCode: values.areaCode,
            phone: values.phone.replace(/\D/g, ''),
            isSoleSigner: values.isSoleSigner,
            inSignsCessionAgreement: values.inSignsCessionAgreement,
            inEndorsementSigner: values.inEndorsementSigner,
            inGuarantor: values.inGuarantor,
            inIssuesPromissoryNote: values.inIssuesPromissoryNote,
            inRelatedParty: values.inRelatedParty,
            inRepresentative: values.inRepresentative
          }
        });
      }

      if (objectHasChanges) setHasChanges(true);

      return {
        ...prevSupplierData,
        personIdOperation: +personIdLogged,
        assignors: updatedAssignorArray
      };
    });

    onClose();
  };

  useEffect(() => {
    if (editMode === true && assignorData) {
      resetAssignor(assignorData);
      if (assignorData.cnpj) {
        setSelectedValue('legalPerson');
      } else {
        setSelectedValue('naturalPerson');
      }
    } else {
      resetAssignor({});
    }
  }, [isAssinorRegistrationModalOpen]);

  return (
    <Modal open={isAssinorRegistrationModalOpen} onClose={onClose} title={'DADOS DA PESSOA CEDENTE'}>
      <form onSubmit={handleSubmitAssignor(handleOnSubmit)} className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <FormLabel component="legend">Tipo de Pessoa</FormLabel>
            <RadioGroup row aria-label="personType" name="personType1" value={selectedValue} onChange={handleChange}>
              <FormControlLabel value="naturalPerson" control={<Radio />} label="Pessoa Física" />
              <FormControlLabel value="legalPerson" control={<Radio />} label="Pessoa Jurídica" />
            </RadioGroup>
          </Grid>
          {selectedValue === 'naturalPerson' && (
            <Grid item xs={12} sm={6} md={6}>
              <Controller
                name="cpf"
                control={controlAssignor}
                defaultValue=""
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    fullWidth
                    variant="outlined"
                    placeholder="CPF"
                    label="CPF"
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      inputComponent: CPFTextMaskCustom as any
                    }}
                  />
                )}
              />
            </Grid>
          )}
          {selectedValue === 'legalPerson' && (
            <Grid item xs={12} sm={6} md={6}>
              <Controller
                name="cnpj"
                control={controlAssignor}
                defaultValue=""
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    fullWidth
                    variant="outlined"
                    placeholder="CNPJ"
                    label="CNPJ"
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      inputComponent: CNPJTextMaskCustom as any
                    }}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="fullName"
              control={controlAssignor}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                  value={value}
                  onChange={(e) => onChange(e.target.value.toUpperCase())}
                  fullWidth
                  variant="outlined"
                  placeholder="Nome completo"
                  label="Nome completo"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="personalEmail"
              control={controlAssignor}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                  value={value}
                  onChange={(e) => onChange(e.target.value.toLowerCase())}
                  fullWidth
                  variant="outlined"
                  placeholder="Email pesssoal"
                  label="Email pesssoal"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={2} sm={2} md={2}>
            <Controller
              name="areaCode"
              control={controlAssignor}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  fullWidth
                  variant="outlined"
                  placeholder="DDD"
                  label="DDD"
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    inputComponent: DDDTextMaskCustom as any
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={10} sm={10} md={10}>
            <Controller
              name="phone"
              control={controlAssignor}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  fullWidth
                  variant="outlined"
                  placeholder="Telefone"
                  label="Telefone"
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    inputComponent: PhoneTextMaskCustom as any
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Typography>Indicadores:</Typography>
            <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '50%' }}>
                {assignorIndicators.slice(0, Math.ceil(assignorIndicators.length / 2)).map((indicator) => (
                  <Controller
                    key={indicator.name}
                    name={indicator.name}
                    control={controlAssignor}
                    defaultValue={indicator.value}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value === 'S'}
                            onChange={(e) => field.onChange(e.target.checked ? 'S' : 'N')}
                            color="primary"
                          />
                        }
                        label={indicator.label}
                      />
                    )}
                  />
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '50%' }}>
                {assignorIndicators.slice(Math.ceil(assignorIndicators.length / 2)).map((indicator) => (
                  <Controller
                    key={indicator.name}
                    name={indicator.name}
                    control={controlAssignor}
                    defaultValue={indicator.value}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value === 'S'}
                            onChange={(e) => field.onChange(e.target.checked ? 'S' : 'N')}
                            color="primary"
                          />
                        }
                        label={indicator.label}
                      />
                    )}
                  />
                ))}
              </div>
            </div>
          </Grid>
          <Grid className={classes.buttonsContainer} item xs={12} sm={12} md={12}>
            <Button variant="text" color="primary" onClick={onClose}>
              CANCELAR
            </Button>
            <Button variant="contained" color="primary" type="submit" disabled={editMode && !isDirtyAssignor}>
              CONFIRMAR
            </Button>
          </Grid>
        </Grid>
      </form>
    </Modal>
  );
};
